import { Psychology } from "./Tabs/Psychology";
import { Strategy } from "./Tabs/Strategy";
import { SynopsisTab } from "./Tabs/Synposis";
import { Visual } from "./Tabs/Visual";

import { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom"; // IMPORT FOR PORTAL

import {formatText} from "../DanvinciHelpers"

import { 
    Brain, Image as ImageIcon,
    Sparkles, Target, Camera, X,
    Zap,
    ChevronLeft, ChevronRight,
    Award, 
} from "lucide-react";

import { getStorageURL } from "../../../firebase";

export const ImageDetailModal = ({ image, onClose, onNext, onPrev, isFirst, isLast }) => {
    const [activeTab, setActiveTab] = useState('synopsis'); 
    const [imageUrl, setImageUrl] = useState(null);

    // --- Data Normalization / Helpers ---
    const access = (path, fallback = null) => {
        const keys = path.split('.');
        let result = image;
        for (const key of keys) {
            if (result && typeof result === 'object' && key in result) {
                result = result[key];
            } else {
                return fallback;
            }
        }
        return result;
    };

    const fitScore = (access('psychological_drivers_analysis.strategic_fit_score') ?? access('strategic_fit_score') ?? 0) * 100;
    const rank = access('psychological_drivers_analysis.rank') ?? access('rank') ?? '#';
    const caption = access('psychological_drivers_analysis.suggested_caption') ?? access('suggested_caption') ?? '';
    const fitColor = fitScore > 80 ? "text-emerald-400" : fitScore > 60 ? "text-yellow-400" : "text-red-400";
    const recommendedMedium = access('psychological_drivers_analysis.recommended_medium') ?? access('recommended_medium') ?? 'N/A';
    const strategicRationale = access('psychological_drivers_analysis.strategic_rationale') ?? access('strategic_rationale') ?? '';
    const timingRationale = access('psychological_drivers_analysis.timing_rationale') ?? access('timing_rationale') ?? '';
    const audience = access('multimodal_coherence_analysis.optimal_audience_segment') ?? access('optimal_audience_segment') ?? 'General Audience';
    const phase = access('multimodal_coherence_analysis.optimal_campaign_phase') ?? access('optimal_campaign_phase') ?? 'General';
    const mediumMatrix = access('composition_analysis.detailed_medium_metrics.medium_compatibility_matrix') ?? access('medium_compatibility_matrix') ?? {};
    
    // Performance Metrics
    const perfMetrics = access('predictive_performance_metrics', {});
    const memoryMetrics = access('memory_encoding_prediction', {});
    const authenticity = access('authenticity_differentiation', {});
    
    // Image Fetch Effect
    useEffect(() => {
        let active = true;
        const fetchUrl = async () => {
            const path = access('multimodal_coherence_analysis.object_path') ?? access('object_path');
            if (path) {
                try {
                    const url = await getStorageURL(path);
                    if (active) { setImageUrl(url); return; }
                } catch (e) { console.warn("Modal SDK load failed", e); }
            }
            
            const displayUrl = access('composition_analysis.detailed_medium_metrics.displayable_url') ?? access('displayable_url');
            if (active && displayUrl) setImageUrl(displayUrl);
        };
        fetchUrl();
        return () => { active = false; };
    }, [image]);


    // Tab Button Component - WHITE ACCENT
    const TabBtn = ({ id, label, icon: Icon }) => (
        <button 
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 ${
                activeTab === id 
                ? 'border-white text-white bg-slate-800/50' 
                : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/30'
            }`}
        >
            {Icon && <Icon size={14} />} {label}
        </button>
    );

    // === HELPER: Random Picker ===
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

    // === HELPER: Neuro-Narrative Generator ===
    const generateNeuroSynthesis = (neural) => {
        if (!neural) return null;
        
        const amygdala = (neural.amygdala_predicted_arousal || 0) * 1000;
        const vmPFC = (neural.vmPFC_predicted_arousal || 0) * 1000;
        const dlPFC = (neural.dlPFC_predicted_arousal || 0) * 1000;
        const insula = (neural.insula_ACC_predicted_arousal || 0) * 1000;
        
        const max = Math.max(amygdala, vmPFC, dlPFC, insula);
        let synthesis = "";
        let sentiment = "neutral";

        if (max === amygdala) {
            synthesis = "This visual primarily triggers an **Emotional Salience** response. Dominant Amygdala activation suggests immediate, visceral engagement—bypassing logic to strike at core fight-or-flight mechanisms.";
            sentiment = "warning";
        } else if (max === vmPFC) {
            synthesis = "This visual primarily triggers a **Value & Reward** response. Strong vmPFC activation indicates the audience instinctively perceives this content as desirable, valuable, or personally resonant.";
            sentiment = "positive";
        } else if (max === dlPFC) {
            synthesis = "This visual primarily triggers a **Cognitive Processing** response. High dlPFC activity means the audience is analyzing, reading, or evaluating the content rather than just 'feeling' it.";
            sentiment = "info";
        } else if (max === insula) {
            synthesis = "This visual primarily triggers an **Empathetic/Sensory** response. High Insula activity often correlates with 'feeling' the image physically (interoception) or strong moral intuition.";
            sentiment = "warning";
        }

        if (amygdala > 50 && vmPFC > 100) {
            synthesis += " **Strategic Note:** The combination of high Emotion (Amygdala) and high Value (vmPFC) is the **'Viral Signature'**—content that is both gripping and rewarding.";
            sentiment = "positive";
        } else if (amygdala > 80 && vmPFC < 50) {
            synthesis += " **Risk Alert:** High emotion without perceived value (low vmPFC) can trigger **'Psychological Avoidance'**—viewers may tune out to protect themselves from distress.";
            sentiment = "negative";
        } else if (dlPFC > 400 && vmPFC < 100) {
            synthesis += " **Risk Alert:** High cognitive load (dlPFC) with low reward (vmPFC) typically leads to **'Scrolling Fatigue'**. The audience is working too hard for too little emotional payoff.";
            sentiment = "negative";
        } else if (dlPFC > 300 && vmPFC > 150) {
             synthesis += " **Outcome:** This pattern represents **'Persuasive Argumentation'**—the audience is thinking critically but finding the conclusion valuable.";
             sentiment = "info";
        }

        return { text: synthesis, sentiment };
    };

    const neuroNarrative = useMemo(() => generateNeuroSynthesis(access('predicted_neural_arousal')), [image]);

    // === MAIN GENERATOR: The Dante Narrative Engine (EXPANDED VARIETY) ===
    const generateDanteSynthesis = (image, semiotics) => {
        if (!image) return "Data insufficient for strategic synthesis.";

        const getVal = (path, fallback) => {
            const keys = path.split('.');
            let result = image;
            for (const key of keys) {
                if (result && typeof result === 'object' && key in result) {
                    result = result[key];
                } else {
                    return fallback;
                }
            }
            return result !== undefined ? result : fallback;
        };

        // Core Metrics
        const arousal = getVal('avg_arousal', 0);
        const valence = getVal('avg_valence', 0);
        
        // --- QUALITATIVE CLARITY LOGIC (Safe Descriptors) ---
        const confidenceScore = getVal('narrative_structure.narrative_confidence', 0.5) ||
                                getVal('composition_analysis.narrative_structure.narrative_confidence', 0.5);
        
        let clarityPhrase = "";
        if (confidenceScore > 0.7) {
            clarityPhrase = pick([
                "unambiguous focus",
                "structural precision",
                "clear visual hierarchy",
                "thematic clarity"
            ]);
        } else if (confidenceScore > 0.4) {
            clarityPhrase = pick([
                "layered complexity",
                "nuanced structure",
                "multi-focal composition",
                "open-ended framing"
            ]);
        } else {
            clarityPhrase = pick([
                "impressionistic style",
                "dynamic visual tension",
                "non-linear narrative",
                "abstract presentation"
            ]);
        }

        // Semantic Variables
        const rawEmotion = (getVal('dominant_emotion', 'neutral')).toLowerCase();
        const emotion = rawEmotion; 
        
        const rhetoric = getVal('visual_rhetoric.rhetorical_appeals.0', '') || "Ethos";
        
        const archetypeObj = getVal('archetype_compatibility_scores.most_resonant', {});
        const archetypeRaw = Object.keys(archetypeObj).length > 0 ? Object.keys(archetypeObj)[0] : "General Audience";
        const archetype = formatText(archetypeRaw);
    
        const phase = formatText(getVal('multimodal_coherence_analysis.optimal_campaign_phase', 'General'));
        const authenticity = getVal('authenticity_differentiation.category', 'GENERIC');
    
        // 2. TEMPLATE BUCKETS (Significantly Expanded to prevent Repetition)
        let partA, partB, partC;
    
        // --- QUADRANT 1: HIGH ENERGY / POSITIVE (Momentum, Action) ---
        if (arousal > 0.6 && valence > 0.3) {
            partA = pick([
                `Radiating energy and optimism, this visual immediately grabs attention through its use of ${emotion}.`,
                `A visually vibrant composition that commands the viewer's eye via intense ${emotion}.`,
                `This image acts as a catalyst for engagement, leveraging positive energy to lower psychological barriers.`,
                `By prioritizing high-arousal ${emotion}, the visual creates an immediate 'pattern interrupt' in the feed.`,
                `The composition is aggressively optimistic, utilizing ${emotion} to drive immediate physiological interest.`
            ]);
    
            partB = pick([
                `The ${clarityPhrase} anchors this energy in the ${archetype} style, creating a sense of momentum.`,
                `By channeling the ${archetype} framework, the composition directs this raw energy into a story defined by ${clarityPhrase}.`,
                `While the energy is high, the ${clarityPhrase} ensures the message remains focused rather than chaotic.`,
                `This raw vibrancy is structurally contained by the ${archetype} narrative, which provides ${clarityPhrase}.`,
                `It converts transient excitement into thematic substance by leveraging ${clarityPhrase} alongside the ${archetype} motif.`
            ]);
    
            partC = pick([
                `Ideally positioned for ${phase} moments where viral shareability is the main goal.`,
                `A tactical asset for high-speed campaigns during the ${phase}, particularly for capturing new audiences.`,
                `Use this as a 'top of funnel' driver during the ${phase} to maximize reach.`,
                `Best deployed when the primary KPI is reach and immediate reaction during the ${phase}.`
            ]);
        } 
        
        // --- QUADRANT 2: STABLE / POSITIVE (Trust, Connection) ---
        else if (arousal <= 0.6 && valence > 0.3) {
            partA = pick([
                `Anchored in stability, this composition establishes a deep sense of ${emotion}.`,
                `This visual operates on a subtle level, inducing a state of receptivity and calm ${emotion}.`,
                `Subtle and assured, the imagery invites the viewer into a space of cognitive ease.`,
                `Rather than demanding attention, this visual earns it through a quiet expression of ${emotion}.`,
                `The visual strategy here is one of restraint, prioritizing ${emotion} to build comfort.`
            ]);
    
            partB = pick([
                `This quiet confidence aligns perfectly with the ${archetype} mindset, aided by its ${clarityPhrase}.`,
                `The narrative uses ${rhetoric} to build a logical bridge, reinforced by ${clarityPhrase}.`,
                `It prioritizes substance over flash, grounding its ${archetype} appeal in ${clarityPhrase}.`,
                `Through ${clarityPhrase}, the image validates the ${archetype} worldview without needing aggressive signaling.`,
                `The ${archetype} framing serves to reinforce existing beliefs, supported by the image's ${clarityPhrase}.`
            ]);
    
            partC = pick([
                `Deploy this asset during the ${phase} to deepen loyalty and keep audiences engaged.`,
                `A critical stabilizer for the ${phase}, perfect for reinforcing trust over quick clicks.`,
                `This is a retention-focused asset, ideal for the ${phase} of the narrative arc.`,
                `Use this to reduce friction and build authority during the ${phase}.`
            ]);
        }
    
        // --- QUADRANT 3: SERIOUS / URGENT (Replaces "Negative/Fear") ---
        // EXPANDED: Removed repetitive "Potent Use" templates
        else if (valence <= 0.3) {
            partA = pick([
                `Projecting ${emotion} with tangible weight, this visual signals importance and gravity.`,
                `The composition creates deliberate focus, utilizing ${emotion} to disrupt the status quo.`,
                `There is a distinct solemnity here, where ${emotion} is used to underscore the stakes.`,
                `Visually uncompromising, the image leverages ${emotion} to arrest the viewer's attention.`,
                `By centering on ${emotion}, the visual establishes an immediate tone of consequence.`,
                `This is a 'lean-in' visual, using the gravity of ${emotion} to demand cognitive processing.`
            ]);
    
            partB = pick([
                `This intensity is channeled through the ${archetype} lens, where ${clarityPhrase} helps guide the viewer's interpretation.`,
                `It uses ${rhetoric} to make the topic feel personal, relying on ${clarityPhrase} to pivot toward a resolution.`,
                `The ${archetype} framing prevents this from being purely somber, using ${clarityPhrase} to suggest a path forward.`,
                `While the tone is heavy, the ${archetype} structure provides a necessary container, aided by ${clarityPhrase}.`,
                `It transforms raw tension into a strategic narrative by applying ${clarityPhrase} to the ${archetype} theme.`
            ]);
    
            partC = pick([
                `A high-stakes asset for the ${phase}—effective when the Call to Action is clear and direct.`,
                `Use sparingly to break through noise during the ${phase}, ensuring a solution is presented alongside it.`,
                `Best utilized to highlight a specific problem or gap during the ${phase}.`,
                `This serves as a 'reality check' in the content mix, vital for the ${phase}.`
            ]);
        }
    
        // --- QUADRANT 4: NEUTRAL / INFORMATIVE ---
        else {
            partA = pick([
                `A highly objective visualization that prioritizes information over emotion.`,
                `Balanced and measured, this image presents the subject matter with professional neutrality.`,
                `This visual eschews emotional manipulation in favor of clear, direct presentation.`,
                `The aesthetic is functional and clean, designed to reduce cognitive load.`
            ]);
    
            partB = pick([
                `The lack of emotional noise allows the ${rhetoric} to stand on merit, supported by ${clarityPhrase}.`,
                `It resonates with the ${archetype} by respecting the viewer's intelligence, utilizing ${clarityPhrase} to organize the data.`,
                `With ${clarityPhrase}, the image acts as a transparent vessel for the ${archetype} message.`,
                `The ${archetype} appeal here is logical rather than emotional, reinforced by ${clarityPhrase}.`
            ]);
    
            partC = pick([
                `Ideal for B2B contexts or the ${phase} where clarity is the primary driver.`,
                `Use this to legitimize claims during the ${phase}—it acts as 'visual proof'.`,
                `A foundational asset for the ${phase}, establishing credibility through objectivity.`
            ]);
        }
    
        // 3. AUTHENTICITY APPEND
        let synthesis = `${partA} ${partB} ${partC}`;
        
        if (authenticity === 'HIGHLY_AUTHENTIC') {
            synthesis += pick([
                " Additionally, the high authenticity signal acts as a trust multiplier here.",
                " Its unpolished authenticity distinguishes it from generic stock content, deepening the connection.",
                " The 'real-world' feel of the imagery is actually its strongest strategic hook.",
                " The lack of artificial polish serves to validate the message's sincerity."
            ]);
        }
    
        return synthesis;
    };

    const danteSummary = useMemo(() => generateDanteSynthesis(image, image?.semiotics_analysis || {}), [image]);

    // PORTAL WRAPPER
    // We create a portal to document.body to ensure the modal breaks out of any 
    // parent containers (like dashboards with overflow:hidden or transforms).
    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={onClose}>
            
            {/* Modal Card */}
            <div className="bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex overflow-hidden flex-col md:flex-row" onClick={(e) => e.stopPropagation()}>
                
                {/* Left: Image Container - LARGER */}
                <div className="w-full md:w-1/2 bg-black relative flex items-center justify-center group">
                    
                    {/* NAV ARROWS: Now strictly inside the image container */}
                    <button onClick={(e) => { e.stopPropagation(); onPrev(); }} disabled={isFirst} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-slate-800/50 text-white hover:bg-slate-700 disabled:opacity-20 backdrop-blur-md transition-all z-20">
                        <ChevronLeft size={24} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onNext(); }} disabled={isLast} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-slate-800/50 text-white hover:bg-slate-700 disabled:opacity-20 backdrop-blur-md transition-all z-20">
                        <ChevronRight size={24} />
                    </button>
                    
                    {imageUrl ? (
                        <img src={imageUrl} alt="Analysis Target" className="max-h-full max-w-full object-contain" />
                    ) : (
                        <ImageIcon size={48} className="text-slate-700" />
                    )}
                    
                    {/* Floating Stats */}
                    <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
                        <span className="bg-black/60 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs font-bold border border-white/10">
                            Rank #{rank}
                        </span>
                        {access('viral_potential_analysis.shareability_category') === 'HIGH' && (
                            <span className="bg-purple-500/80 backdrop-blur-md text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                                <Zap size={12} /> Viral Potential
                            </span>
                        )}
                         {authenticity.category === 'HIGHLY_AUTHENTIC' && (
                            <span className="bg-emerald-500/80 backdrop-blur-md text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                                <Award size={12} /> Authentic
                            </span>
                        )}
                    </div>

                    {/* Strategic Fit Badge */}
                    <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md border border-white/10 rounded-xl p-3 z-10">
                        <div className="text-xs text-slate-400 mb-1">Strategic Fit</div>
                        <div className={`text-3xl font-black ${fitColor} font-mono`}>{fitScore.toFixed(0)}%</div>
                    </div>
                </div>

                {/* Right: Data Panel */}
                <div className="w-full md:w-1/2 flex flex-col bg-slate-900">
                    
                    {/* Header */}
                    <div className="p-6 border-b border-slate-700/50 flex justify-between items-start bg-slate-900/50 backdrop-blur-md z-10">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-xl font-bold text-white">PRISM Vision Analysis</h3>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                                <span className="text-slate-400">Target:</span>
                                <span className="text-white font-semibold">{audience}</span>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors">
                            <X size={20} />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-slate-700/50 bg-slate-900/30">
                        <TabBtn id="synopsis" label="Synopsis" icon={Sparkles} />
                        <TabBtn id="psychology" label="Psychology" icon={Brain} />
                        <TabBtn id="strategy" label="Strategy" icon={Target} />
                        <TabBtn id="visual" label="Visual DNA" icon={Camera} />
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-slate-900/20">
                        {activeTab === 'synopsis' && <SynopsisTab 
                        danteSummary={danteSummary} 
                        access={access} 
                        timingRationale={timingRationale} 
                        strategicRationale={strategicRationale}
                        phase={phase}
                        audience={audience}
                        rank={rank}
                        fitScore={fitScore}
                        ></SynopsisTab>}

                        {activeTab === 'psychology' && <Psychology
                            memoryMetrics={memoryMetrics}
                        authenticity={authenticity}
                        neuroNarrative={neuroNarrative}
                        access={access}
                        ></Psychology>}

                        {activeTab === 'strategy' && <Strategy
                         phase={phase}
                         mediumMatrix={mediumMatrix}
                         audience={audience}
                         authenticity={authenticity}
                         perfMetrics={perfMetrics}
                         access={access}
                         recommendedMedium={recommendedMedium}
                        strategicRationale={strategicRationale}
                        ></Strategy>}

                        {activeTab === 'visual' && <Visual
                        authenticity={authenticity}
                        access={access}
                        ></Visual>}
                    </div>
                </div>
            </div>
        </div>,
        document.body // Target for the portal
    );
};